/**
 * Aleluya Wishlist — client-side JS
 * Communicates with the wishlist backend API (persists to customer metafield wishlist.data).
 *
 * v2 (WISHLIST FEATURES DEV):
 *  - Price drop alerts      → item.price_alert  {active, target_price, variant_id, base_price, set_at}
 *  - Back in stock alerts   → item.stock_alert  {active, variant_ids[], set_at}
 *  - Share list             → /pages/wishlist-shared?id={token}  (token from backend /api/wishlist/share)
 *  - Analytics              → gtag('event') / window.dataLayer + 'aleluya:wishlist' DOM event
 *  - Live availability/price from /products/{handle}.js, add to bag from the wishlist
 *
 * Alerts are stored inside each wishlist item (the backend keeps unknown item fields),
 * and mirrored to localStorage `aleluya_wishlist_alerts_{customerId}` with the
 * metafield-ready shape {productId, variant, targetPrice, alertActive}.
 */
(function() {
  'use strict';

  const API_BASE = 'https://aleluya-wishlist-bac-4yj1f6.abacusai.app';
  const STORAGE_KEY = 'aleluya_wishlist';
  const ALERTS_KEY_PREFIX = 'aleluya_wishlist_alerts_';
  const DEFAULT_LIST_ID = 'default';
  const DEFAULT_LIST_NAME = 'Favoritos';
  const SHARE_PAGE_PATH = '/pages/wishlist-shared';
  // Email alerts service (price drop / back in stock). Key is public by design; the
  // per-customer HMAC signature (__aleluya_customer_sig) is what authorises each call.
  const NOTIFY_BASE = 'https://aleluya-returns.abacusai.app';
  const NOTIFY_KEY = '04c393c07e86e38780b75d0fe5d805e76cdd206aca400eeb';
  const CURRENCY = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';

  // --- State ---
  let wishlistData = { lists: [], shares: {} };
  let sessionToken = null;
  let isLoggedIn = false;
  let isSyncing = false;
  let syncQueued = false;
  let localVersion = 0;
  const liveProducts = new Map(); // product_id -> /products/{handle}.js payload (never persisted)

  // --- Helpers ---
  function getLocalData() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  function saveLocalData(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {}
  }

  function ensureDefaultList(data) {
    if (!data.lists) data.lists = [];
    if (!data.shares) data.shares = {};
    if (!data.lists.find(l => l.id === DEFAULT_LIST_ID)) {
      data.lists.unshift({ id: DEFAULT_LIST_ID, name: DEFAULT_LIST_NAME, items: [] });
    }
    return data;
  }

  function getDefaultList() {
    return wishlistData.lists.find(l => l.id === DEFAULT_LIST_ID);
  }

  function findItem(productId) {
    const list = getDefaultList();
    if (!list) return null;
    return list.items.find(i => String(i.product_id) === String(productId)) || null;
  }

  function isProductInList(productId, listId) {
    listId = listId || DEFAULT_LIST_ID;
    const list = wishlistData.lists.find(l => l.id === listId);
    if (!list) return false;
    return list.items.some(i => String(i.product_id) === String(productId));
  }

  function getAllProductIds() {
    const ids = new Set();
    (wishlistData.lists || []).forEach(l => {
      (l.items || []).forEach(i => ids.add(String(i.product_id)));
    });
    return ids;
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    if (cents == null || cents === '' || isNaN(Number(cents))) return '';
    const amount = (Number(cents) / 100).toFixed(2);
    return '€' + amount.replace('.', ',');
  }

  function formatDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  function getCustomerId() {
    return window.__aleluya_customer_id ? String(window.__aleluya_customer_id) : null;
  }

  // --- Analytics ---
  // GA4 is loaded with gtag.js (G-09SRKPVJR). gtag() pushes into window.dataLayer.
  // Without gtag (e.g. a future GTM setup) we push a GTM-style {event: ...} object instead,
  // never both, so events are not double counted.
  function track(eventName, params) {
    const payload = Object.assign({ currency: CURRENCY, wishlist_size: getAllProductIds().size }, params || {});
    window.dataLayer = window.dataLayer || [];
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      } else {
        window.dataLayer.push(Object.assign({ event: eventName }, payload));
      }
    } catch (e) { /* analytics must never break the UI */ }
    try {
      document.dispatchEvent(new CustomEvent('aleluya:wishlist', { detail: { event: eventName, params: payload } }));
    } catch (e) {}
    try {
      if (localStorage.getItem('aleluya_wl_debug')) console.log('[Wishlist][track]', eventName, payload);
    } catch (e) {}
  }

  function gaItem(productId, variantId, priceCents, title) {
    const item = { item_id: String(productId) };
    if (title) item.item_name = title;
    if (variantId) item.item_variant = String(variantId);
    if (priceCents != null && priceCents !== '' && !isNaN(Number(priceCents))) item.price = Number(priceCents) / 100;
    return item;
  }

  function priceParams(productId, variantId, priceCents, title) {
    const p = { product_id: String(productId), variant_id: variantId ? String(variantId) : null };
    if (priceCents != null && priceCents !== '' && !isNaN(Number(priceCents))) {
      p.price = Number(priceCents) / 100;
      p.value = p.price;
    }
    p.items = [gaItem(productId, variantId, priceCents, title)];
    return p;
  }

  // --- Alerts mirror (localStorage fallback, metafield-ready shape) ---
  function alertsKey() {
    const cid = getCustomerId();
    return cid ? ALERTS_KEY_PREFIX + cid : null;
  }

  function readAlertMirror() {
    const key = alertsKey();
    if (!key) return null;
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  function buildAlertMirror() {
    const list = getDefaultList();
    const items = list ? list.items : [];
    const prev = readAlertMirror() || {};
    const keepInactive = (arr, activeIds) => (arr || []).filter(a => !a.alertActive && !activeIds.has(String(a.productId)));
    const price = [];
    const stock = [];
    items.forEach(i => {
      if (i.price_alert) {
        price.push({
          productId: i.product_id,
          variant: i.price_alert.variant_id || i.variant_id || null,
          targetPrice: i.price_alert.target_price != null ? i.price_alert.target_price : null,
          alertActive: !!i.price_alert.active,
          setAt: i.price_alert.set_at || null,
          handle: i.handle || null,
        });
      }
      if (i.stock_alert) {
        stock.push({
          productId: i.product_id,
          variants: i.stock_alert.variant_ids || [],
          alertActive: !!i.stock_alert.active,
          setAt: i.stock_alert.set_at || null,
          handle: i.handle || null,
        });
      }
    });
    const priceIds = new Set(price.map(a => String(a.productId)));
    const stockIds = new Set(stock.map(a => String(a.productId)));
    return {
      customer_id: getCustomerId(),
      updated_at: new Date().toISOString(),
      price_alerts: price.concat(keepInactive(prev.price_alerts, priceIds)).slice(0, 200),
      stock_alerts: stock.concat(keepInactive(prev.stock_alerts, stockIds)).slice(0, 200),
      share_token: (wishlistData.shares && wishlistData.shares[DEFAULT_LIST_ID]) || prev.share_token || null,
    };
  }

  function writeAlertMirror() {
    const key = alertsKey();
    if (!key) return;
    try { localStorage.setItem(key, JSON.stringify(buildAlertMirror())); } catch {}
  }

  // If a sync failed (offline / API down) the server copy may miss the latest alert change.
  // Re-apply mirror entries that are newer than what the server returned.
  function reconcileAlertsFromMirror() {
    const mirror = readAlertMirror();
    const list = getDefaultList();
    if (!mirror || !list) return false;
    let changed = false;
    const newer = (a, b) => (a || '') > (b || '');
    (mirror.price_alerts || []).forEach(m => {
      const item = list.items.find(i => String(i.product_id) === String(m.productId));
      if (!item) return;
      const cur = item.price_alert;
      if (!cur || newer(m.setAt, cur.set_at)) {
        item.price_alert = { active: !!m.alertActive, target_price: m.targetPrice, variant_id: m.variant || null, base_price: cur ? cur.base_price : null, set_at: m.setAt };
        changed = true;
      }
    });
    (mirror.stock_alerts || []).forEach(m => {
      const item = list.items.find(i => String(i.product_id) === String(m.productId));
      if (!item) return;
      const cur = item.stock_alert;
      if (!cur || newer(m.setAt, cur.set_at)) {
        item.stock_alert = { active: !!m.alertActive, variant_ids: m.variants || [], set_at: m.setAt };
        changed = true;
      }
    });
    return changed;
  }

  // --- Session Token ---
  async function getSessionToken() {
    // Method 1: New Customer Accounts session token
    try {
      const resp = await fetch('/customer_authentication/session_token', {
        method: 'GET',
        credentials: 'same-origin',
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data && data.session_token) {
          sessionToken = data.session_token;
          isLoggedIn = true;
          return sessionToken;
        }
      }
    } catch {}

    // Method 2: Liquid-injected customer ID + HMAC signature
    if (window.__aleluya_customer_id && window.__aleluya_customer_sig) {
      isLoggedIn = true;
      return null;
    }

    // Method 3: customer ID without signature (read-only, local only)
    if (window.__aleluya_customer_id) {
      isLoggedIn = true;
    }

    return null;
  }

  function canSync() {
    return !!(sessionToken || window.__aleluya_customer_sig);
  }

  // --- API calls ---
  async function apiCall(path, method, body) {
    const headers = { 'Content-Type': 'application/json' };
    if (sessionToken) {
      headers['Authorization'] = 'Bearer ' + sessionToken;
    } else if (window.__aleluya_customer_id && window.__aleluya_customer_sig) {
      headers['X-Customer-Id'] = String(window.__aleluya_customer_id);
      headers['X-Customer-Sig'] = window.__aleluya_customer_sig;
    }
    const opts = { method, headers };
    if (body) opts.body = JSON.stringify(body);
    try {
      const resp = await fetch(API_BASE + path, opts);
      return await resp.json();
    } catch (err) {
      console.warn('[Wishlist] API error:', err);
      return null;
    }
  }

  // Changes made while a sync is in flight are queued (previously they were dropped).
  async function syncToServer() {
    if (!canSync()) return;
    if (isSyncing) { syncQueued = true; return; }
    isSyncing = true;
    const sentVersion = localVersion;
    try {
      const result = await apiCall('/api/wishlist/sync', 'POST', wishlistData);
      if (result && result.success && result.data && sentVersion === localVersion) {
        wishlistData = ensureDefaultList(result.data);
        saveLocalData(wishlistData);
      }
    } finally {
      isSyncing = false;
      if (syncQueued) { syncQueued = false; syncToServer(); }
    }
  }

  function markDirty() {
    localVersion++;
    saveLocalData(wishlistData);
    writeAlertMirror();
  }

  async function loadFromServer() {
    if (!canSync()) return;
    try {
      const result = await apiCall('/api/wishlist/sync', 'GET');
      if (result && result.success && result.data) {
        // Merge: server data wins, but keep local additions
        const serverData = ensureDefaultList(result.data);
        const localData = getLocalData();
        if (localData && localData.lists) {
          localData.lists.forEach(localList => {
            let serverList = serverData.lists.find(l => l.id === localList.id);
            if (!serverList) {
              serverData.lists.push(localList);
            } else {
              localList.items.forEach(localItem => {
                if (!serverList.items.some(si => String(si.product_id) === String(localItem.product_id))) {
                  serverList.items.push(localItem);
                }
              });
            }
          });
        }
        wishlistData = serverData;
        reconcileAlertsFromMirror();
        saveLocalData(wishlistData);
        writeAlertMirror();
        await syncToServer();
        backfillNotify();
      }
    } catch (err) {
      console.warn('[Wishlist] Load from server error:', err);
    }
  }

  // --- Toggle product ---
  function toggleProduct(productId, productData, source) {
    productData = productData || {};
    if (!isLoggedIn) {
      window.location.href = '/account/login?return_to=' + encodeURIComponent(window.location.pathname);
      return;
    }

    const list = getDefaultList();
    if (!list) return;

    const idx = list.items.findIndex(i => String(i.product_id) === String(productId));
    if (idx >= 0) {
      const removed = list.items.splice(idx, 1)[0];
      if (removed.price_alert && removed.price_alert.active) notifyCall('DELETE', '/api/subscribe/price-alert', { product_id: Number(removed.product_id) });
      if (removed.stock_alert && removed.stock_alert.active) notifyCall('DELETE', '/api/subscribe/stock-alert', { product_id: Number(removed.product_id) });
      const live = liveProducts.get(String(productId));
      track('wishlist_remove', Object.assign(
        priceParams(productId, removed.variant_id, live ? live.price : removed.last_price, removed.title),
        { source: source || 'unknown', had_price_alert: !!(removed.price_alert && removed.price_alert.active), had_stock_alert: !!(removed.stock_alert && removed.stock_alert.active) }
      ));
    } else {
      const item = {
        product_id: Number(productId),
        variant_id: productData.variant_id || null,
        added_at: new Date().toISOString(),
        last_price: productData.price ? Number(productData.price) : null,
        handle: productData.handle || null,
        title: productData.title || null,
        image_url: productData.image_url || null,
      };
      list.items.push(item);
      track('wishlist_add', Object.assign(priceParams(productId, item.variant_id, item.last_price, item.title), { source: source || 'unknown' }));
    }

    markDirty();
    updateAllButtons();
    updateWishlistCount();
    updateStats();
    syncToServer();
  }

  // --- Email alerts service sync ---
  function absUrl(u) {
    if (!u) return null;
    return u.indexOf('//') === 0 ? 'https:' + u : u;
  }

  async function notifyCall(method, path, body) {
    const cid = getCustomerId();
    const sig = window.__aleluya_customer_sig;
    if (!cid || !sig) return false;
    try {
      const resp = await fetch(NOTIFY_BASE + path, {
        method: method,
        headers: { 'Content-Type': 'application/json', 'X-Notify-Key': NOTIFY_KEY, 'X-Customer-Sig': sig },
        body: JSON.stringify(Object.assign({ customer_id: Number(cid) }, body || {})),
        keepalive: true,
      });
      if (!resp.ok) console.warn('[Wishlist] notify', method, path, resp.status);
      return resp.ok;
    } catch (e) {
      console.warn('[Wishlist] notify error', path, e);
      return false;
    }
  }

  function pushPriceAlert(item, rearm) {
    const a = item && item.price_alert;
    if (!a) return;
    if (!a.active || a.target_price == null) {
      return notifyCall('DELETE', '/api/subscribe/price-alert', { product_id: Number(item.product_id) });
    }
    const base = a.base_price != null ? a.base_price : item.last_price;
    if (base == null) return;
    return notifyCall('POST', '/api/subscribe/price-alert', {
      product_id: Number(item.product_id),
      variant_id: a.variant_id ? Number(a.variant_id) : null,
      target_price_cents: Math.round(Number(a.target_price)),
      price_at_sub_cents: Math.round(Number(base)),
      product_title: item.title || null,
      product_handle: item.handle || null,
      product_image_url: absUrl(item.image_url),
      rearm: rearm !== false,
    });
  }

  function pushStockAlert(item, rearm) {
    const a = item && item.stock_alert;
    if (!a) return;
    if (!a.active) {
      return notifyCall('DELETE', '/api/subscribe/stock-alert', { product_id: Number(item.product_id) });
    }
    return notifyCall('POST', '/api/subscribe/stock-alert', {
      product_id: Number(item.product_id),
      variant_ids: (a.variant_ids || []).map(Number).filter(Boolean),
      product_title: item.title || null,
      product_handle: item.handle || null,
      product_image_url: absUrl(item.image_url),
      rearm: rearm !== false,
    });
  }

  // One-time (per browser session) backfill of alerts that already exist in the wishlist,
  // so alerts set before the email service existed also get emails. Never re-arms.
  function backfillNotify() {
    const cid = getCustomerId();
    if (!cid || !wishlistData || !wishlistData.lists) return;
    const flag = 'aleluya_notify_backfill_' + cid;
    try { if (sessionStorage.getItem(flag)) return; sessionStorage.setItem(flag, '1'); } catch {}
    wishlistData.lists.forEach(l => (l.items || []).forEach(i => {
      if (i.price_alert && i.price_alert.active) pushPriceAlert(i, false);
      if (i.stock_alert && i.stock_alert.active) pushStockAlert(i, false);
    }));
  }

  // --- Alerts API ---
  function setPriceAlert(productId, active, targetCents, opts) {
    const item = findItem(productId);
    if (!item) return null;
    const live = liveProducts.get(String(productId));
    const variant = live ? pickVariant(live, item) : null;
    const current = variant ? variant.price : (live ? live.price : item.last_price);
    const prev = item.price_alert || {};
    const target = targetCents != null && !isNaN(targetCents) ? Math.round(targetCents) : (prev.target_price != null ? prev.target_price : current);
    item.price_alert = {
      active: !!active,
      target_price: target != null ? Number(target) : null,
      variant_id: variant ? variant.id : (item.variant_id || null),
      base_price: current != null ? Number(current) : null,
      set_at: new Date().toISOString(),
    };
    markDirty();
    syncToServer();
    pushPriceAlert(item, true);
    if (!opts || !opts.silent) {
      track('wishlist_price_alert_toggle', Object.assign(
        priceParams(productId, item.price_alert.variant_id, current, item.title),
        { alert_active: !!active, target_price: target != null ? Number(target) / 100 : null }
      ));
    }
    updateStats();
    return item.price_alert;
  }

  function setStockAlert(productId, active, variantIds) {
    const item = findItem(productId);
    if (!item) return null;
    const ids = (variantIds || []).map(Number).filter(Boolean);
    item.stock_alert = { active: !!active && ids.length > 0, variant_ids: ids, set_at: new Date().toISOString() };
    markDirty();
    syncToServer();
    pushStockAlert(item, true);
    track('wishlist_stock_alert_toggle', Object.assign(
      priceParams(productId, ids[0] || null, item.last_price, item.title),
      { alert_active: item.stock_alert.active, variant_ids: ids.map(String) }
    ));
    updateStats();
    return item.stock_alert;
  }

  // --- UI Updates ---
  function updateAllButtons() {
    document.querySelectorAll('.wishlist-btn').forEach(btn => {
      var el = btn;
      if (!el.getAttribute('data-wishlist-product-id')) {
        el = el.closest('[data-wishlist-product-id]');
      }
      if (!el) return;
      const pid = el.getAttribute('data-wishlist-product-id');
      if (isProductInList(pid)) {
        btn.classList.add('wishlist-btn--active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('wishlist-btn--active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });
  }

  function updateWishlistCount() {
    const countEls = document.querySelectorAll('.wishlist-count');
    const total = getAllProductIds().size;
    countEls.forEach(el => {
      el.textContent = total;
      el.style.display = total > 0 ? '' : 'none';
    });
  }

  // Refresh the "Wishlist activity" block (snippets/aleluya-wishlist-stats.liquid) from client state
  function updateStats() {
    const box = document.querySelector('[data-wl-stats]');
    if (!box || !isLoggedIn) return;
    const list = getDefaultList();
    const items = list ? list.items : [];
    let first = null;
    let alerts = 0;
    let purchased = 0;
    const ordered = box.getAttribute('data-wl-purchased-ids') || ',';
    items.forEach(i => {
      if (i.added_at && (!first || i.added_at < first)) first = i.added_at;
      if ((i.price_alert && i.price_alert.active) || (i.stock_alert && i.stock_alert.active)) alerts++;
      if (ordered.indexOf(',' + i.product_id + ',') !== -1) purchased++;
    });
    const set = (k, v) => { const el = box.querySelector('[data-wl-stat="' + k + '"]'); if (el) el.textContent = v; };
    set('total', items.length);
    set('first', first ? formatDate(first) : '—');
    set('alerts', alerts);
    set('purchased', purchased);
  }

  // --- Live product data ---
  async function fetchLive(item) {
    if (!item || !item.handle) return null;
    const key = String(item.product_id);
    if (liveProducts.has(key)) return liveProducts.get(key);
    try {
      const resp = await fetch('/products/' + encodeURIComponent(item.handle) + '.js', { credentials: 'same-origin' });
      if (!resp.ok) return null;
      const p = await resp.json();
      liveProducts.set(key, p);
      return p;
    } catch (e) {
      console.warn('[Wishlist] live fetch failed:', item.handle, e);
      return null;
    }
  }

  function pickVariant(live, item) {
    if (!live || !live.variants || !live.variants.length) return null;
    if (item && item.variant_id) {
      const v = live.variants.find(x => String(x.id) === String(item.variant_id));
      if (v) return v;
    }
    return live.variants.find(v => v.available) || live.variants[0];
  }

  function stockState(live) {
    if (!live) return 'unknown';
    if (!live.available) return 'soldout';
    if ((live.variants || []).some(v => !v.available)) return 'partial';
    return 'available';
  }

  function variantLabel(live, id) {
    const v = live && live.variants ? live.variants.find(x => String(x.id) === String(id)) : null;
    return v ? (v.public_title || v.title) : String(id);
  }

  // --- Share ---
  function buildShareUrl(token) {
    let url = window.location.origin + SHARE_PAGE_PATH + '?id=' + encodeURIComponent(token);
    // While testing on an unpublished theme, keep the recipient on the same preview theme
    const theme = window.Shopify && window.Shopify.theme;
    if (theme && theme.role && theme.role !== 'main' && theme.id) url += '&preview_theme_id=' + theme.id;
    return url;
  }

  async function shareList(listId) {
    listId = listId || DEFAULT_LIST_ID;
    let token = wishlistData.shares && wishlistData.shares[listId];
    if (!token) {
      if (!canSync()) return null;
      const result = await apiCall('/api/wishlist/share', 'POST', { list_id: listId });
      if (!result || !result.success) return null;
      token = result.token || (result.url ? String(result.url).split('/').pop() : null);
      if (!token) return null;
      wishlistData.shares = wishlistData.shares || {};
      wishlistData.shares[listId] = token; // backend already stored it in wishlist.data.shares
      saveLocalData(wishlistData);
      writeAlertMirror();
    }
    return buildShareUrl(token);
  }

  // --- Wishlist page rendering ---
  const ICON_BELL = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>';

  function cloneAlertsMarkup(productId) {
    const ssr = document.querySelector('template[data-wl-ssr-alerts="' + productId + '"]');
    const generic = document.getElementById('wl-tpl-alerts');
    const tpl = ssr || generic;
    if (!tpl) return '';
    const wrap = document.createElement('div');
    wrap.appendChild(tpl.content.cloneNode(true));
    wrap.querySelectorAll('[data-product-id]').forEach(el => el.setAttribute('data-product-id', productId));
    return wrap.innerHTML;
  }

  function renderWishlistPage() {
    const container = document.getElementById('wishlist-page-container');
    if (!container) return;

    if (!isLoggedIn) {
      container.innerHTML = `
        <div class="wishlist-gate">
          <p class="wishlist-gate__eyebrow">WISHLIST</p>
          <h1 class="wishlist-gate__title">Sign in to<br>view your wishlist</h1>
          <p class="wishlist-gate__sub">Save your favourite pieces and get notified when they go on sale.</p>
          <a href="/customer_authentication/login?return_to=/pages/wishlist" class="wishlist-gate__btn">LOG IN TO YOUR ACCOUNT</a>
        </div>`;
      return;
    }

    const defaultList = getDefaultList();

    if (!defaultList || defaultList.items.length === 0) {
      container.innerHTML = `
        <div class="wishlist-empty">
          <p class="wishlist-empty__eyebrow">MY WISHLIST</p>
          <h1 class="wishlist-empty__title">Your wishlist is empty</h1>
          <p class="wishlist-empty__sub">Browse our collections and tap the heart icon to save pieces you love.</p>
          <a href="/collections/all" class="wishlist-empty__btn">SHOP NOW →</a>
        </div>`;
      return;
    }

    let html = `<div class="wishlist-page">
      <div class="wishlist-page__header">
        <div>
          <p class="wishlist-page__eyebrow">MY WISHLIST</p>
          <h1 class="wishlist-page__title">${defaultList.items.length} ${defaultList.items.length === 1 ? 'PIECE' : 'PIECES'}</h1>
        </div>
        <button type="button" class="wishlist-share-btn" data-list-id="${DEFAULT_LIST_ID}">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>
          SHARE LIST
        </button>
      </div>
      <div class="wishlist-grid">`;

    defaultList.items.forEach(item => {
      const pid = esc(item.product_id);
      const imgSrc = item.image_url || '';
      const title = esc(item.title || 'Product');
      const url = item.handle ? '/products/' + encodeURIComponent(item.handle) : '#';
      html += `
        <div class="wishlist-card" data-product-id="${pid}">
          <a href="${url}" class="wishlist-card__image-link">
            <div class="wishlist-card__image-wrap">
              ${imgSrc ? `<img src="${esc(imgSrc)}" alt="${title}" loading="lazy">` : '<div class="wishlist-card__placeholder"></div>'}
              <span class="wishlist-card__badges" data-wl-badges></span>
              <span class="wishlist-card__bell" data-wl-bell title="Alert on" aria-label="Alert on">${ICON_BELL}</span>
            </div>
          </a>
          <div class="wishlist-card__info">
            <a href="${url}" class="wishlist-card__title">${title}</a>
            <span class="wishlist-card__price" data-wl-price>${esc(item.price_display || formatMoney(item.last_price))}</span>
          </div>
          <div class="wishlist-card__alerts">${cloneAlertsMarkup(pid)}</div>
          <div class="wishlist-card__cart" data-wl-cart hidden>
            <select class="wishlist-card__variant" data-wl-cart-variant aria-label="Choose size" hidden></select>
            <button type="button" class="wishlist-card__add" data-wl-add-to-cart>ADD TO BAG</button>
            <p class="wishlist-card__cart-msg" data-wl-cart-msg aria-live="polite"></p>
          </div>
          <button type="button" class="wishlist-card__remove" data-remove-product="${pid}" aria-label="Remove from wishlist">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>`;
    });

    html += `</div></div>`;

    html += `
      <div class="wishlist-share-modal" id="wishlist-share-modal" style="display:none" role="dialog" aria-modal="true" aria-labelledby="wishlist-share-title">
        <div class="wishlist-share-modal__overlay"></div>
        <div class="wishlist-share-modal__content">
          <h3 id="wishlist-share-title">Share your wishlist</h3>
          <p class="wishlist-share-modal__desc">Anyone with this link can see the pieces on your list — never your name or email.</p>
          <div class="wishlist-share-modal__url-row">
            <input type="text" id="wishlist-share-url" readonly aria-label="Share link">
            <button type="button" id="wishlist-copy-url">COPY</button>
          </div>
          <button type="button" class="wishlist-share-modal__native" id="wishlist-native-share" hidden>SHARE VIA…</button>
          <button type="button" class="wishlist-share-modal__close" id="wishlist-share-close">CLOSE</button>
        </div>
      </div>`;

    container.innerHTML = html;
    container.querySelectorAll('.wishlist-card').forEach(card => {
      const item = findItem(card.getAttribute('data-product-id'));
      if (item) applyCardState(card, item);
    });
    bindWishlistPageEvents(container);
  }

  function applyCardState(card, item) {
    const pid = String(item.product_id);
    const live = liveProducts.get(pid);
    const pa = item.price_alert && item.price_alert.active ? item.price_alert : null;
    const sa = item.stock_alert && item.stock_alert.active ? item.stock_alert : null;
    const stockEl = card.querySelector('[data-wl-stock-alert]');
    let state = live ? stockState(live) : (stockEl ? stockEl.getAttribute('data-state') : 'unknown');

    card.classList.toggle('wishlist-card--soldout', state === 'soldout');
    card.classList.toggle('wishlist-card--partial', state === 'partial');
    card.classList.toggle('wishlist-card--price-alert', !!pa);
    card.classList.toggle('wishlist-card--stock-alert', !!sa);
    card.classList.toggle('wishlist-card--alert', !!(pa || sa));
    const bell = card.querySelector('[data-wl-bell]');
    if (bell) {
      const label = pa && sa ? 'Price & stock alerts on' : pa ? 'Price alert on' : 'Back in stock alert on';
      bell.title = label; bell.setAttribute('aria-label', label);
    }

    // Price + badges
    const variant = live ? pickVariant(live, item) : null;
    const current = variant ? variant.price : (live ? live.price : item.last_price);
    const saved = item.last_price;
    const dropped = live && saved && current < saved;
    const priceEl = card.querySelector('[data-wl-price]');
    if (priceEl && current != null) {
      priceEl.innerHTML = dropped
        ? `<span class="wishlist-card__price-now">${formatMoney(current)}</span> <s class="wishlist-card__price-was">${formatMoney(saved)}</s>`
        : esc(formatMoney(current));
    }
    const badges = card.querySelector('[data-wl-badges]');
    if (badges) {
      let b = '';
      if (state === 'soldout') b += '<span class="wl-badge wl-badge--soldout">SOLD OUT</span>';
      if (dropped) b += '<span class="wl-badge wl-badge--drop">PRICE DROP</span>';
      badges.innerHTML = b;
    }

    // Price alert control
    const priceBox = card.querySelector('[data-wl-price-alert]');
    if (priceBox) {
      const toggle = priceBox.querySelector('[data-wl-price-toggle]');
      const row = priceBox.querySelector('[data-wl-price-target-row]');
      const input = priceBox.querySelector('[data-wl-price-target]');
      const status = priceBox.querySelector('[data-wl-price-status]');
      priceBox.classList.toggle('is-active', !!pa);
      if (toggle) toggle.checked = !!pa;
      if (row) row.hidden = !pa;
      const target = pa && pa.target_price != null ? pa.target_price : current;
      if (input && document.activeElement !== input && target != null) input.value = (Number(target) / 100).toFixed(2);
      if (status) {
        const reached = pa && live && current != null && pa.target_price != null && current < pa.target_price;
        status.classList.toggle('is-reached', !!reached);
        status.textContent = !pa ? '' : reached
          ? 'Price dropped — now ' + formatMoney(current)
          : 'Alert on · we\u2019ll tell you when it drops below ' + formatMoney(target);
      }
    }

    // Stock alert control
    if (stockEl) {
      stockEl.setAttribute('data-state', state);
      ['available', 'partial', 'soldout', 'unknown'].forEach(s => stockEl.classList.toggle('wl-stock--' + s, s === state));
      stockEl.classList.toggle('is-active', !!sa);
      const labelText = stockEl.querySelector('[data-wl-stock-label-text]');
      if (labelText) labelText.textContent = { soldout: 'Sold out', partial: 'Some sizes sold out', available: 'In stock', unknown: 'Checking availability…' }[state];
      const picker = stockEl.querySelector('[data-wl-stock-picker]');
      const select = stockEl.querySelector('[data-wl-stock-variant]');
      const btn = stockEl.querySelector('[data-wl-stock-toggle]');
      const btnText = stockEl.querySelector('[data-wl-stock-btn-text]');
      const status = stockEl.querySelector('[data-wl-stock-status]');
      const monitored = sa ? sa.variant_ids.map(String) : [];
      if (picker) picker.hidden = state !== 'partial';
      if (select && live && state === 'partial') {
        const prevVal = select.value;
        select.innerHTML = live.variants.filter(v => !v.available)
          .map(v => `<option value="${v.id}">${esc(v.public_title || v.title)}${monitored.indexOf(String(v.id)) !== -1 ? ' ✓' : ''}</option>`).join('');
        if (prevVal && select.querySelector('option[value="' + prevVal + '"]')) select.value = prevVal;
      }
      if (btn) {
        btn.hidden = !(state === 'soldout' || state === 'partial');
        let pressed = !!sa;
        let text = sa ? 'Alert on · Cancel' : 'Notify me when it\u2019s back';
        if (state === 'partial' && select) {
          const selected = select.value;
          pressed = monitored.indexOf(String(selected)) !== -1;
          text = pressed ? 'Cancel alert for this size' : 'Notify me when this size is back';
        }
        btn.setAttribute('aria-pressed', String(pressed));
        btn.classList.toggle('is-pressed', pressed);
        if (btnText) btnText.textContent = text;
      }
      if (status) {
        status.textContent = !sa ? '' : (state === 'partial' || (live && monitored.length < live.variants.length))
          ? 'Watching: ' + monitored.map(id => variantLabel(live, id)).join(', ')
          : 'We\u2019ll let you know when it\u2019s back';
        if (sa && state === 'available') status.textContent = 'Back in stock — grab it now';
      }
    }

    // Add to bag
    const cartBox = card.querySelector('[data-wl-cart]');
    if (cartBox) {
      const available = live ? live.variants.filter(v => v.available) : [];
      cartBox.hidden = !live || available.length === 0;
      const sel = cartBox.querySelector('[data-wl-cart-variant]');
      if (sel && live) {
        const multi = live.variants.length > 1 && available.length > 0;
        sel.hidden = !multi;
        if (multi && !sel.options.length) {
          sel.innerHTML = available.map(v => `<option value="${v.id}"${item.variant_id && String(item.variant_id) === String(v.id) ? ' selected' : ''}>${esc(v.public_title || v.title)}</option>`).join('');
        }
        if (!multi && available[0]) cartBox.setAttribute('data-variant-id', available[0].id);
      }
    }
  }

  function refreshCard(productId) {
    const card = document.querySelector('.wishlist-card[data-product-id="' + productId + '"]');
    const item = findItem(productId);
    if (card && item) applyCardState(card, item);
  }

  function parseMoneyInput(value) {
    const n = parseFloat(String(value).replace(',', '.'));
    return isNaN(n) || n <= 0 ? null : Math.round(n * 100);
  }

  async function addToCartFromWishlist(card) {
    const pid = card.getAttribute('data-product-id');
    const item = findItem(pid);
    const live = liveProducts.get(String(pid));
    const cartBox = card.querySelector('[data-wl-cart]');
    const sel = card.querySelector('[data-wl-cart-variant]');
    const btn = card.querySelector('[data-wl-add-to-cart]');
    const msg = card.querySelector('[data-wl-cart-msg]');
    const variantId = (sel && !sel.hidden && sel.value) || (cartBox && cartBox.getAttribute('data-variant-id'));
    if (!variantId) return;
    btn.disabled = true;
    btn.textContent = 'ADDING…';
    if (msg) msg.textContent = '';
    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.description || data.message || 'Could not add to bag');
      const v = live ? live.variants.find(x => String(x.id) === String(variantId)) : null;
      track('wishlist_add_to_cart', Object.assign(
        priceParams(pid, variantId, v ? v.price : (item && item.last_price), item && item.title),
        { source: 'wishlist_page', quantity: 1 }
      ));
      btn.textContent = 'ADDED ✓';
      if (msg) msg.innerHTML = '<a href="/cart">View bag →</a>';
      try {
        if (window.PXUTheme && window.PXUTheme.jsAjaxCart) {
          window.PXUTheme.jsAjaxCart.updateView();
          window.PXUTheme.jsAjaxCart.showDrawer();
        }
      } catch (e) {}
      setTimeout(() => { btn.textContent = 'ADD TO BAG'; btn.disabled = false; }, 2200);
    } catch (err) {
      btn.textContent = 'ADD TO BAG';
      btn.disabled = false;
      if (msg) msg.textContent = err.message;
    }
  }

  function openShareModal(url) {
    const modal = document.getElementById('wishlist-share-modal');
    const input = document.getElementById('wishlist-share-url');
    if (!modal || !input) return;
    input.value = url;
    modal.style.display = 'flex';
    const nativeBtn = document.getElementById('wishlist-native-share');
    if (nativeBtn) nativeBtn.hidden = !navigator.share;
    input.focus();
    input.select();
  }

  function closeShareModal() {
    const modal = document.getElementById('wishlist-share-modal');
    if (modal) modal.style.display = 'none';
  }

  function bindWishlistPageEvents(container) {
    if (container.__wlBound) return;
    container.__wlBound = true;

    container.addEventListener('click', async function(e) {
      const removeBtn = e.target.closest('.wishlist-card__remove');
      if (removeBtn) {
        e.preventDefault();
        toggleProduct(removeBtn.getAttribute('data-remove-product'), {}, 'wishlist_page');
        renderWishlistPage();
        return;
      }

      const stockBtn = e.target.closest('[data-wl-stock-toggle]');
      if (stockBtn) {
        e.preventDefault();
        const card = stockBtn.closest('.wishlist-card');
        const pid = card.getAttribute('data-product-id');
        const item = findItem(pid);
        const live = liveProducts.get(String(pid));
        if (!item) return;
        const box = stockBtn.closest('[data-wl-stock-alert]');
        const state = box.getAttribute('data-state');
        const current = item.stock_alert && item.stock_alert.active ? item.stock_alert.variant_ids.map(String) : [];
        if (state === 'partial') {
          const selected = String((box.querySelector('[data-wl-stock-variant]') || {}).value || '');
          if (!selected) return;
          const next = current.indexOf(selected) !== -1 ? current.filter(id => id !== selected) : current.concat(selected);
          setStockAlert(pid, next.length > 0, next);
        } else if (current.length) {
          setStockAlert(pid, false, []);
        } else {
          let ids = item.variant_id ? [item.variant_id] : [];
          if (!ids.length && live) ids = live.variants.filter(v => !v.available).map(v => v.id);
          if (!ids.length && live) ids = live.variants.map(v => v.id);
          if (!ids.length) ids = [item.product_id]; // last resort: product-level watch
          setStockAlert(pid, true, ids);
        }
        refreshCard(pid);
        return;
      }

      const addBtn = e.target.closest('[data-wl-add-to-cart]');
      if (addBtn) {
        e.preventDefault();
        addToCartFromWishlist(addBtn.closest('.wishlist-card'));
        return;
      }

      const shareBtn = e.target.closest('.wishlist-share-btn');
      if (shareBtn) {
        shareBtn.disabled = true;
        const url = await shareList(shareBtn.getAttribute('data-list-id'));
        shareBtn.disabled = false;
        if (!url) {
          alert('Sorry, we could not create a share link right now. Please try again.');
          return;
        }
        track('wishlist_share', { share_method: 'link_generated', item_count: (getDefaultList() || { items: [] }).items.length });
        openShareModal(url);
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(() => {
            const copyBtn = document.getElementById('wishlist-copy-url');
            if (copyBtn) { copyBtn.textContent = 'COPIED!'; setTimeout(() => { copyBtn.textContent = 'COPY'; }, 2000); }
          }).catch(() => {});
        }
        return;
      }

      if (e.target.closest('#wishlist-copy-url')) {
        const input = document.getElementById('wishlist-share-url');
        const copyBtn = document.getElementById('wishlist-copy-url');
        input.select();
        const done = () => { copyBtn.textContent = 'COPIED!'; setTimeout(() => { copyBtn.textContent = 'COPY'; }, 2000); };
        if (navigator.clipboard) navigator.clipboard.writeText(input.value).then(done).catch(() => { document.execCommand('copy'); done(); });
        else { document.execCommand('copy'); done(); }
        track('wishlist_share', { share_method: 'copy' });
        return;
      }

      if (e.target.closest('#wishlist-native-share')) {
        const url = document.getElementById('wishlist-share-url').value;
        try {
          await navigator.share({ title: 'My Aleluya wishlist', url: url });
          track('wishlist_share', { share_method: 'native' });
        } catch (err) {}
        return;
      }

      if (e.target.closest('#wishlist-share-close') || e.target.closest('.wishlist-share-modal__overlay')) {
        closeShareModal();
      }
    });

    container.addEventListener('change', function(e) {
      const toggle = e.target.closest('[data-wl-price-toggle]');
      if (toggle) {
        const card = toggle.closest('.wishlist-card');
        const pid = card.getAttribute('data-product-id');
        const input = card.querySelector('[data-wl-price-target]');
        const existing = (findItem(pid) || {}).price_alert;
        const target = existing && existing.target_price != null ? existing.target_price : (input ? parseMoneyInput(input.value) : null);
        setPriceAlert(pid, toggle.checked, target);
        refreshCard(pid);
        return;
      }
      const targetInput = e.target.closest('[data-wl-price-target]');
      if (targetInput) {
        const card = targetInput.closest('.wishlist-card');
        const pid = card.getAttribute('data-product-id');
        const cents = parseMoneyInput(targetInput.value);
        if (cents) setPriceAlert(pid, true, cents, { silent: true });
        refreshCard(pid);
        return;
      }
      const stockSelect = e.target.closest('[data-wl-stock-variant]');
      if (stockSelect) refreshCard(stockSelect.closest('.wishlist-card').getAttribute('data-product-id'));
    });

    document.addEventListener('keydown', function(e) { if (e.key === 'Escape') closeShareModal(); });
  }

  // --- Public shared wishlist page (/pages/wishlist-shared?id=TOKEN) ---
  async function renderSharedPage() {
    const container = document.getElementById('wishlist-shared-container');
    if (!container) return;
    const token = new URLSearchParams(window.location.search).get('id') || '';
    const unavailable = (msg) => {
      container.innerHTML = `
        <div class="wl-shared__empty">
          <p class="wl-shared__eyebrow">SHARED WISHLIST</p>
          <h1 class="wl-shared__title">${esc(msg)}</h1>
          <p class="wl-shared__sub">The link may have expired or been mistyped.</p>
          <a href="/collections/all" class="wl-shared__btn">DISCOVER ALELUYA →</a>
        </div>`;
    };
    if (!/^[A-Za-z0-9_-]{8,128}$/.test(token)) { unavailable('This wishlist is not available'); return; }

    let data = null;
    try {
      const resp = await fetch(API_BASE + '/api/wishlist/shared/' + encodeURIComponent(token));
      data = await resp.json();
    } catch (e) { data = null; }
    if (!data || !data.success) { unavailable('This wishlist is not available'); return; }

    const products = data.products || [];
    const isOwner = !!(wishlistData.shares && Object.values(wishlistData.shares).indexOf(token) !== -1);
    track('wishlist_public_view', { share_id: token.slice(0, 6), item_count: products.length, is_owner: isOwner });

    if (!products.length) {
      container.innerHTML = `
        <div class="wl-shared__empty">
          <p class="wl-shared__eyebrow">SHARED WISHLIST</p>
          <h1 class="wl-shared__title">This wishlist is empty</h1>
          <a href="/collections/all" class="wl-shared__btn">DISCOVER ALELUYA →</a>
        </div>`;
      return;
    }

    let html = `
      <div class="wl-shared__header">
        <p class="wl-shared__eyebrow">SHARED WISHLIST</p>
        <h1 class="wl-shared__title">${products.length} ${products.length === 1 ? 'PIECE' : 'PIECES'}<span class="wl-shared__dot">.</span></h1>
        <p class="wl-shared__sub">A selection of favourites from the Aleluya store.</p>
      </div>
      <div class="wishlist-grid wl-shared__grid">`;
    products.forEach(p => {
      const url = p.handle ? '/products/' + encodeURIComponent(p.handle) : '#';
      const priceCents = p.price != null ? Math.round(parseFloat(p.price) * 100) : null;
      html += `
        <div class="wishlist-card wl-shared__card" data-product-id="${esc(p.product_id)}" data-handle="${esc(p.handle || '')}">
          <a href="${url}" class="wishlist-card__image-link" data-wl-shared-link>
            <div class="wishlist-card__image-wrap">
              ${p.image_url ? `<img src="${esc(p.image_url)}" alt="${esc(p.title)}" loading="lazy">` : '<div class="wishlist-card__placeholder"></div>'}
              <span class="wishlist-card__badges" data-wl-badges></span>
            </div>
          </a>
          <div class="wishlist-card__info">
            <a href="${url}" class="wishlist-card__title" data-wl-shared-link>${esc(p.title)}</a>
            <span class="wishlist-card__price" data-wl-price>${esc(formatMoney(priceCents))}</span>
          </div>
          <a href="${url}" class="wl-shared__view" data-wl-shared-link>VIEW PIECE →</a>
        </div>`;
    });
    html += `</div>
      <div class="wl-shared__cta">
        <p>Want your own list? Tap the heart on any piece.</p>
        <a href="/collections/all" class="wl-shared__btn">START YOUR WISHLIST →</a>
      </div>`;
    container.innerHTML = html;

    container.addEventListener('click', function(e) {
      const link = e.target.closest('[data-wl-shared-link]');
      if (!link) return;
      const card = link.closest('.wl-shared__card');
      track('wishlist_public_click', { share_id: token.slice(0, 6), product_id: card ? card.getAttribute('data-product-id') : null });
    });

    // Live availability / price (storefront AJAX API)
    await Promise.all(products.map(async p => {
      if (!p.handle) return;
      const live = await fetchLive({ product_id: p.product_id, handle: p.handle });
      const card = container.querySelector('.wl-shared__card[data-product-id="' + p.product_id + '"]');
      if (!live || !card) return;
      const priceEl = card.querySelector('[data-wl-price]');
      if (priceEl) priceEl.textContent = formatMoney(live.price);
      if (!live.available) {
        card.classList.add('wishlist-card--soldout');
        const b = card.querySelector('[data-wl-badges]');
        if (b) b.innerHTML = '<span class="wl-badge wl-badge--soldout">SOLD OUT</span>';
      }
    }));
  }

  // --- Init ---
  async function init() {
    const local = getLocalData();
    wishlistData = ensureDefaultList(local || { lists: [], shares: {} });

    updateAllButtons();
    updateWishlistCount();

    await getSessionToken();

    if (canSync()) {
      await loadFromServer();
      updateAllButtons();
      updateWishlistCount();
    }
    updateStats();

    if (document.getElementById('wishlist-page-container')) {
      renderWishlistPage(); // instant paint with saved data
      await enrichWishlistItems();
      renderWishlistPage(); // repaint with live price / availability
    }

    if (document.getElementById('wishlist-shared-container')) {
      renderSharedPage();
    }
  }

  async function enrichWishlistItems() {
    const list = getDefaultList();
    const items = list ? list.items : [];
    if (!items.length) return;
    let changed = false;

    await Promise.all(items.map(async item => {
      if (item.handle) {
        const live = await fetchLive(item);
        if (live) {
          if (!item.title) { item.title = live.title; changed = true; }
          if (!item.image_url && live.featured_image) { item.image_url = live.featured_image; changed = true; }
          const display = formatMoney(live.price);
          if (item.price_display !== display) { item.price_display = display; changed = true; }
          return;
        }
      }
      // Fallback: backend lookup by product_id (Admin API)
      if (item.product_id && !item.title) {
        try {
          const resp2 = await fetch(API_BASE + '/api/wishlist/product-info?id=' + item.product_id);
          if (resp2.ok) {
            const pdata = await resp2.json();
            if (pdata && pdata.handle) {
              item.title = pdata.title || item.title;
              item.handle = pdata.handle;
              item.image_url = pdata.image_url || item.image_url;
              item.price_display = pdata.price_display || item.price_display;
              changed = true;
              await fetchLive(item);
            }
          }
        } catch (e) { console.warn('[Wishlist] enrich by API failed:', e); }
      }
    }));

    if (changed) saveLocalData(wishlistData);
  }

  // --- Event delegation for heart buttons ---
  document.addEventListener('click', function(e) {
    const btn = e.target.closest('.wishlist-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var el = btn;
    if (!el.getAttribute('data-wishlist-product-id')) {
      el = el.closest('[data-wishlist-product-id]');
    }
    if (!el) return;
    const pid = el.getAttribute('data-wishlist-product-id');
    const pdata = {
      variant_id: null,
      price: el.getAttribute('data-wishlist-product-price'),
      title: el.getAttribute('data-wishlist-product-title'),
      handle: el.getAttribute('data-wishlist-product-handle'),
      image_url: el.getAttribute('data-wishlist-product-image'),
    };
    const source = btn.classList.contains('wishlist-btn--thumbnail') ? 'product_thumbnail' : 'product_page';
    toggleProduct(pid, pdata, source);
  });

  // --- Wishlist product added to cart from the product page / quick shop ---
  document.addEventListener('click', function(e) {
    const addBtn = e.target.closest('[data-add-to-cart-trigger], .button--add-to-cart');
    if (!addBtn || addBtn.disabled) return;
    const formWrap = addBtn.closest('[data-product-id]');
    if (!formWrap) return;
    const pid = formWrap.getAttribute('data-product-id');
    if (!isProductInList(pid)) return;
    const form = addBtn.closest('form');
    const idInput = form ? form.querySelector('[name="id"]') : null;
    const qtyInput = form ? form.querySelector('[name="quantity"]') : null;
    const item = findItem(pid);
    track('wishlist_add_to_cart', Object.assign(
      priceParams(pid, idInput ? idInput.value : null, item && item.last_price, item && item.title),
      { source: 'product_page', quantity: qtyInput ? Number(qtyInput.value) || 1 : 1 }
    ));
  }, true);

  // --- Expose globally ---
  window.AleluyaWishlist = {
    toggle: toggleProduct,
    isInWishlist: isProductInList,
    getData: () => wishlistData,
    share: shareList,
    setPriceAlert: setPriceAlert,
    setStockAlert: setStockAlert,
    getAlerts: () => buildAlertMirror(),
    track: track,
    refresh: () => { updateAllButtons(); updateWishlistCount(); updateStats(); },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
